'use client'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { BalanceGrant } from '@/lib/billing/types'
import { formatCredits } from '@/lib/billing/utils/credits'
import { grantSourceLabel } from '@/lib/billing/wallet/balance-details'

/** Full date in the viewer's time zone: expiries can be a year away. */
export function formatExpiryDate(iso: string, timezone: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: timezone })
}

export function BalanceDetailsDialog({
  open,
  onOpenChange,
  grants,
  timezone,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  grants: BalanceGrant[]
  timezone: string
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Balance details</DialogTitle>
          <DialogDescription>Credits that expire first are used first.</DialogDescription>
        </DialogHeader>
        <ul className="divide-y">
          {grants.map((grant, index) => (
            // Grants are listed once per open, in spending order, and never reordered.
            <li key={index} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2">
              <div className="min-w-0">
                <p className="break-words text-sm font-medium">{grantSourceLabel(grant.source, grant.planName)}</p>
                <p className="text-xs text-muted-foreground">
                  {grant.expiresAt ? `Expires ${formatExpiryDate(grant.expiresAt, timezone)}` : 'No expiry'}
                </p>
              </div>
              <span className="font-mono text-sm">{formatCredits(grant.remainingMicrousd)}</span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  )
}
